import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function Servicos() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProjectsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const projectsSection = document.getElementById('projetos');
      if (projectsSection) {
        projectsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contato');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Projetos Residenciais em Ilhabela',
      description: 'Desenvolvemos projetos arquitetônicos completos para residências em Ilhabela, desde o estudo preliminar até o projeto executivo. Cada etapa é cuidadosamente planejada para atender às necessidades do cliente e às características do terreno litorâneo.',
      features: [
        'Estudo preliminar e viabilidade',
        'Anteprojeto arquitetônico',
        'Projeto executivo completo',
        'Compatibilização com projetos complementares',
        'Aprovação em órgãos competentes'
      ]
    },
    {
      icon: 'ri-ship-line',
      title: 'Arquitetura Litorânea Especializada',
      description: 'Trabalhamos com soluções específicas para casas no litoral, priorizando ventilação natural, iluminação adequada, materiais apropriados e integração entre interior e exterior. Nossa experiência em Ilhabela garante projetos adaptados ao clima e à paisagem.',
      features: [
        'Ventilação cruzada e conforto térmico',
        'Materiais resistentes à maresia',
        'Grandes aberturas e integração visual',
        'Proteção solar adequada',
        'Aproveitamento de vistas panorâmicas'
      ]
    },
    {
      icon: 'ri-tools-line',
      title: 'Gestão e Acompanhamento de Obra',
      description: 'Oferecemos acompanhamento técnico de obra, garantindo que o projeto arquitetônico seja executado com fidelidade, controle e qualidade. Realizamos visitas periódicas e orientamos a equipe de execução.',
      features: [
        'Visitas técnicas periódicas',
        'Controle de qualidade da execução',
        'Orientação à equipe de obra',
        'Verificação de conformidade com projeto',
        'Solução de imprevistos técnicos'
      ]
    },
    {
      icon: 'ri-landscape-line',
      title: 'Projetos para Terrenos Inclinados',
      description: 'Especialização em projetos para terrenos com topografia desafiadora, comum em Ilhabela. Transformamos declives em vantagens arquitetônicas, criando múltiplos níveis que aproveitam as vistas e a paisagem.',
      features: [
        'Análise topográfica detalhada',
        'Soluções de contenção e terraplanagem',
        'Aproveitamento de desníveis',
        'Múltiplos níveis integrados',
        'Minimização de movimentação de terra'
      ]
    },
    {
      icon: 'ri-pencil-ruler-line',
      title: 'Reforma e Ampliação',
      description: 'Desenvolvemos projetos de reforma e ampliação para residências existentes em Ilhabela, modernizando espaços e adequando a arquitetura às novas necessidades dos moradores.',
      features: [
        'Levantamento da edificação existente',
        'Projeto de reforma e modernização',
        'Ampliação de áreas',
        'Adequação à legislação atual',
        'Valorização do imóvel'
      ]
    },
    {
      icon: 'ri-plant-line',
      title: 'Paisagismo Integrado',
      description: 'Projetos de paisagismo que complementam a arquitetura, utilizando vegetação nativa e adequada ao clima litorâneo de Ilhabela, criando jardins que valorizam a residência.',
      features: [
        'Seleção de espécies adequadas',
        'Vegetação nativa e adaptada',
        'Integração com arquitetura',
        'Áreas de convivência externa',
        'Manutenção facilitada'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} onProjectsClick={handleProjectsClick} onContactClick={handleContactClick} />
      
      <section className="pt-32 pb-20 bg-sand">
        <div className="max-w-[1200px] mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-playfair text-6xl lg:text-7xl font-light text-gray-900 mb-6">
              Serviços de <strong className="font-bold">Arquitetura</strong>
            </h1>
            <p className="font-inter text-xl text-gray-600 max-w-3xl leading-relaxed">
              Soluções completas em arquitetura para projetos residenciais em Ilhabela, do conceito à execução.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="grid lg:grid-cols-2 gap-12 items-start"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-2xl mb-6">
                    <i className={`${service.icon} text-4xl text-accent`}></i>
                  </div>
                  <h2 className="font-playfair text-3xl text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="font-inter text-base text-gray-700 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <i className="ri-check-line text-accent text-xl mt-0.5"></i>
                        <span className="font-inter text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`rounded-3xl overflow-hidden ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <img
                    src={`https://public.readdy.ai/ai/img_res/71b89d1168b57e044a3b0fd866c630f6.jpg$%7Bservice.title.toLowerCase%28%29%7D%20modern%20residential%20project%20in%20coastal%20area%2C%20contemporary%20design%2C%20high%20quality%20architectural%20photography%2C%20clean%20minimalist%20style%2C%20simple%20background&width=800&height=600&seq=service-${index + 10}&orientation=landscape`}
                    alt={`${service.title} - Criarquitetura Ilhabela`}
                    title={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-sand">
        <div className="max-w-[1200px] mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-playfair text-4xl text-gray-900 mb-6">
              Pronto para iniciar seu projeto?
            </h2>
            <p className="font-inter text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              Entre em contato para agendar uma conversa e conhecer como nossos serviços podem transformar seu projeto em realidade.
            </p>
            <a
              href="https://wa.me/5512999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-4 bg-accent text-white text-base font-inter font-medium rounded-full hover:bg-accent/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Solicitar Orçamento
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
